import { useEffect, useState } from 'react';
import {
  Chip,
  DiffView,
  FilterBar,
  Icon,
  MetaGrid,
  MetaItem,
  Panel,
  RuleId,
  Screen,
  ScreenHeader,
  StatusBadge,
  Tabs,
  Workspace,
  WorkspacePane,
} from '@garnet/ui';
import type { AuditEntry, DiffLineKind } from '../data/types.js';
import { KIND_BADGE, type StudioContext } from '../studio.js';
import { StateBlock } from '../components.js';

const MARK: Record<DiffLineKind, 'add' | 'del' | 'ctx'> = { add: 'add', del: 'del', ctx: 'ctx' };

function DiffPane({
  ctx,
  ruleId,
  setRuleId,
}: {
  ctx: StudioContext;
  ruleId: string;
  setRuleId: (id: string) => void;
}) {
  const { api } = ctx;
  const diffable = api.diffableRuleIds();
  const d = api.diff(ruleId);
  const adds = d ? d.lines.filter((l) => l.t === 'add').length : 0;
  const dels = d ? d.lines.filter((l) => l.t === 'del').length : 0;

  return (
    <Workspace style={{ gridTemplateColumns: '1fr 300px' }}>
      <WorkspacePane flush>
      <Panel
        flush
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <Icon name="branch" size={13} color="var(--fg-2)" />
            <span className="ruleid">{ruleId}</span>
            {d && (
              <span className="mono metatext">
                {d.from} → {d.to}
              </span>
            )}
          </span>
        }
        right={
          d && (
            <span style={{ display: 'flex', gap: 8 }}>
              <span className="mono" style={{ fontSize: 11, color: 'var(--status-pass-text)' }}>
                +{adds}
              </span>
              <span className="mono" style={{ fontSize: 11, color: 'var(--status-fail-text)' }}>
                −{dels}
              </span>
            </span>
          )
        }
      >
        {d ? (
          <div style={{ background: 'var(--bg-sunken)' }}>
            <DiffView lines={d.lines.map((l) => ({ type: MARK[l.t], text: l.s || '​' }))} />
            <div
              style={{
                padding: '10px 12px',
                borderTop: '1px solid var(--border-subtle)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span className="metatext mono">
                {d.date} · {d.author} → {d.approver}
              </span>
              <button className="btn btn-sm mono" onClick={() => ctx.openRule(ruleId)}>
                <Icon name="arrow" size={12} />
                Open rule
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: 18 }}>
            <StateBlock kind="empty" title="no structural diff" body="Select a rule with more than one version to compare." />
          </div>
        )}
      </Panel>
      </WorkspacePane>
      <WorkspacePane>
      <Panel flush title="Versioned rules">
        <div>
          {diffable.map((id) => {
            const r = api.rule(id);
            const dd = api.diff(id);
            return (
              <div
                key={id}
                onClick={() => setRuleId(id)}
                role="button"
                style={{
                  padding: '9px var(--pad-x)',
                  borderBottom: '1px solid var(--border-subtle)',
                  cursor: 'pointer',
                  background: id === ruleId ? 'var(--garnet-muted)' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <RuleId id={id} size={11} />
                  {dd && (
                    <span className="mono metatext">
                      {dd.from}→{dd.to}
                    </span>
                  )}
                </div>
                <div className="metatext" style={{ marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r?.title}
                </div>
              </div>
            );
          })}
        </div>
      </Panel>
      </WorkspacePane>
    </Workspace>
  );
}

function RevisitQueue({ ctx }: { ctx: StudioContext }) {
  const { api } = ctx;
  const changed = api.sources().filter((s) => s.watch === 'changed');
  const flagged = changed.reduce((n, s) => n + s.deps.length, 0);
  return (
    <Panel
      flush
      title="Revisit queue"
      right={
        <span className="badge b-warn">
          <span className="dot pulse" style={{ background: 'var(--status-warn-text)' }} />
          {flagged} flagged
        </span>
      }
    >
      <div>
        {changed.map((s) => (
          <div key={s.id} style={{ padding: '10px var(--pad-x)', borderBottom: '1px solid var(--border-subtle)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5, cursor: 'pointer' }} role="button" onClick={() => ctx.openSource(s.id)}>
              <Icon name="alert" size={12} color="var(--status-warn-text)" />
              <span className="mono" style={{ fontSize: 11.5, color: 'var(--garnet-text)' }}>
                {s.citation}
              </span>
            </div>
            <div className="metatext" style={{ marginBottom: 7 }}>
              {s.change}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {s.deps.map((dep) => (
                <span key={dep} onClick={() => ctx.openRule(dep)} role="button" className="ruleid-chip" style={{ padding: '1px 6px' }}>
                  <span className="ruleid" style={{ fontSize: 10.5 }}>
                    {dep}
                  </span>
                </span>
              ))}
            </div>
          </div>
        ))}
        <div className="metatext" style={{ padding: '10px var(--pad-x)', display: 'flex', gap: 6, alignItems: 'center' }}>
          <Icon name="history" size={12} color="var(--fg-3)" />
          Drift latency this quarter:{' '}
          <span className="mono" style={{ color: 'var(--fg-1)' }}>
            {api.meta().driftLatency}
          </span>
        </div>
      </div>
    </Panel>
  );
}

export function Audit({ ctx, initialDiffRule }: { ctx: StudioContext; initialDiffRule: string | null }) {
  const { api } = ctx;
  const [tab, setTab] = useState<'timeline' | 'diff'>(initialDiffRule ? 'diff' : 'timeline');
  const [diffRule, setDiffRule] = useState(initialDiffRule || 'EU-MDR-AnnexVIII-R12');
  const [filter, setFilter] = useState<'all' | AuditEntry['kind']>('all');
  useEffect(() => {
    if (initialDiffRule) {
      setTab('diff');
      setDiffRule(initialDiffRule);
    }
  }, [initialDiffRule]);

  const audit = api.audit();
  const events = filter === 'all' ? audit : audit.filter((e) => e.kind === filter);
  const pubCount = audit.filter((e) => e.kind === 'publish').length;
  const kinds: ('all' | AuditEntry['kind'])[] = ['all', 'publish', 'review', 'watch', 'release', 'rollback', 'draft'];
  const meta = api.meta();

  return (
    <Screen workspace>
      <ScreenHeader
        eyebrow="§ Change & audit"
        title="Append-only"
        lead="Every rule change versioned, attributed, and dated. A living legal artefact."
        actions={
          <Tabs
            tabs={[
              { key: 'timeline', label: 'Timeline' },
              { key: 'diff', label: 'Version diff' },
            ]}
            active={tab}
            onChange={setTab}
          />
        }
      />

      {tab === 'timeline' ? (
        <Workspace style={{ gridTemplateColumns: '1fr 320px' }}>
          <WorkspacePane>
            <div className="pane-sticky" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px var(--pad-x)', flexWrap: 'wrap' }}>
              <span className="panel-title">Audit timeline</span>
              <FilterBar>
                {kinds.map((k) => (
                  <Chip key={k} active={filter === k} onClick={() => setFilter(k)}>
                    {k}
                  </Chip>
                ))}
              </FilterBar>
            </div>
            <div>
              {events.map((e) => {
                const m = KIND_BADGE[e.kind];
                const isRule = api.rule(e.target);
                const isSource = api.source(e.target);
                return (
                  <div
                    key={e.id}
                    style={{ display: 'grid', gridTemplateColumns: '108px 1fr', gap: 12, padding: '11px var(--pad-x)', borderBottom: '1px solid var(--border-subtle)' }}
                  >
                    <div className="mono metatext" style={{ fontSize: 10.5, lineHeight: 1.5 }}>
                      {e.ts}
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <span className={`badge ${m.cls}`}>
                          <Icon name={m.icon} size={10} />
                          {e.action}
                        </span>
                        {isRule ? (
                          <span className="ruleid-chip" style={{ padding: '1px 6px', cursor: 'pointer' }} role="button" onClick={() => ctx.openRule(e.target)}>
                            <span className="ruleid" style={{ fontSize: 10.5 }}>
                              {e.target}
                            </span>
                          </span>
                        ) : isSource ? (
                          <span className="mono cite-link" style={{ fontSize: 11 }} role="button" onClick={() => ctx.openSource(e.target)}>
                            {isSource.citation}
                          </span>
                        ) : (
                          <span className="mono" style={{ fontSize: 11, color: 'var(--garnet-text)' }}>
                            {e.target}
                          </span>
                        )}
                        {e.from && (
                          <span className="mono metatext">
                            {e.from}
                            {e.to ? ' → ' + e.to : ''}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--fg-2)' }}>{e.note}</div>
                      <div className="metatext mono" style={{ fontSize: 10.5, marginTop: 3 }}>
                        {e.actor}
                        {e.affected > 0 && <span> · affects {e.affected.toLocaleString()} consumer records</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </WorkspacePane>
          <WorkspacePane flush>
            <Panel title="This release">
              <MetaGrid labelWidth={150}>
                <MetaItem label="Release">
                  <span className="mono" style={{ color: 'var(--garnet-text)' }}>
                    {meta.release}
                  </span>
                </MetaItem>
                <MetaItem label="Rule changes">
                  <span className="mono">{pubCount} published</span>
                </MetaItem>
                <MetaItem label="Citation completeness">
                  <span className="mono" style={{ color: 'var(--status-warn-text)' }}>
                    {meta.citationCompleteness}%
                  </span>
                </MetaItem>
                <MetaItem label="Determinism">
                  <span className="mono" style={{ color: 'var(--status-pass-text)' }}>
                    {meta.determinism}%
                  </span>
                </MetaItem>
              </MetaGrid>
            </Panel>
            <div style={{ marginTop: 'var(--gap)' }}>
              <RevisitQueue ctx={ctx} />
            </div>
          </WorkspacePane>
        </Workspace>
      ) : (
        <DiffPane ctx={ctx} ruleId={diffRule} setRuleId={setDiffRule} />
      )}
    </Screen>
  );
}
